import { useEffect, useMemo, useRef, useState } from 'react'
import Drawer from './Drawer'
import { useNotifications } from '@/lib/notifications'

export type Conversion = {
  id: string
  from: string
  to: string
  factor: number
  note?: string
}

export type ProductLite = {
  id: number
  treeType: string
  species: string
  subSpecies?: string
  product: string
  productType: string
  conversions?: Conversion[]
}

const UNITS = ['tons', 'cords', 'm³', 'MBF', 'units'] as const

type Props = {
  open: boolean
  product: ProductLite | null
  onClose: () => void
  onSave: (productId: number, conversions: Conversion[]) => void
}

export default function UnitConversionsDrawer({ open, product, onClose, onSave }: Props) {
  const { push } = useNotifications()
  const [rows, setRows] = useState<Conversion[]>([])
  const [testValue, setTestValue] = useState<string>('1')
  const [testConvId, setTestConvId] = useState<string>('')

  const fileRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (product) {
      const start = (product.conversions || []).map(r => ({ ...r }))
      setRows(start)
      setTestConvId(start[0]?.id || '')
    } else {
      setRows([])
      setTestConvId('')
    }
  }, [product])

  const title = useMemo(() => {
    if (!product) return 'Unit Conversions'
    return `Unit Conversions — ${product.product} • ${product.species} (${product.productType})`
  }, [product])

  function addRow() {
    setRows(r => [
      ...r,
      {
        id: crypto.randomUUID(),
        from: 'tons',
        to: 'cords',
        factor: 0.4,
        note: ''
      }
    ])
  }

  function removeRow(id: string) {
    setRows(r => r.filter(x => x.id !== id))
    if (testConvId === id) setTestConvId('')
  }

  function setRow(id: string, key: keyof Conversion, val: any) {
    setRows(r => r.map(x => (x.id === id ? { ...x, [key]: val } : x)))
  }

  function invertRow(id: string) {
    setRows(r =>
      r.map(x =>
        x.id === id && x.factor !== 0
          ? { ...x, from: x.to, to: x.from, factor: 1 / x.factor }
          : x,
      ),
    )
  }

  function applyPreset(preset: 'pine-basic' | 'hardwood-basic') {
    // Example values; verify for your operation.
    const pine: Conversion[] = [
      { id: crypto.randomUUID(), from: 'tons', to: 'cords', factor: 0.385, note: '≈2.6 tons/cord' },
      { id: crypto.randomUUID(), from: 'cords', to: 'tons', factor: 2.60, note: 'Inverse' },
      { id: crypto.randomUUID(), from: 'tons', to: 'm³', factor: 0.907, note: 'Example' },
    ]
    const hardwood: Conversion[] = [
      { id: crypto.randomUUID(), from: 'tons', to: 'cords', factor: 0.345, note: '≈2.9 tons/cord' },
      { id: crypto.randomUUID(), from: 'cords', to: 'tons', factor: 2.90, note: 'Inverse' },
      { id: crypto.randomUUID(), from: 'tons', to: 'm³', factor: 0.90, note: 'Example' },
    ]
    setRows(preset === 'pine-basic' ? pine : hardwood)
    push({ title: 'Preset applied', body: 'Verify factors for your operation.' })
  }

  function save() {
    if (!product) return
    for (const r of rows) {
      if (!r.from || !r.to || !Number.isFinite(r.factor)) {
        push({ title: 'Invalid row', body: 'Each row needs From, To and a numeric factor.', level: 'warning' })
        return
      }
    }
    onSave(product.id, rows)
    push({ title: 'Unit conversions saved' })
    onClose()
  }

  // Export / Import
  function downloadJSON(filename: string, data: unknown) {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  function exportSet() {
    const name = (product?.product || 'product').replace(/\s+/g, '-').toLowerCase()
    downloadJSON(`${name}-conversions.json`, rows)
  }

  async function importSet(file: File) {
    try {
      const text = await file.text()
      const data = JSON.parse(text)
      if (!Array.isArray(data)) throw new Error('Expected an array')
      const norm: Conversion[] = data.map((r: any) => ({
        id: r.id || crypto.randomUUID(),
        from: r.from,
        to: r.to,
        factor: Number(r.factor),
        note: r.note || ''
      }))
      // quick validation
      for (const r of norm) {
        if (!r.from || !r.to || !Number.isFinite(r.factor)) {
          throw new Error('Invalid row found')
        }
      }
      setRows(norm)
      setTestConvId(norm[0]?.id || '')
      push({ title: 'Imported', body: `${norm.length} conversion(s)` })
    } catch (e: any) {
      console.error(e)
      push({ title: 'Import failed', body: e?.message || 'Could not import file', level: 'error' })
    } finally {
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const testResult = useMemo(() => {
    const v = Number(testValue)
    const c = rows.find(r => r.id === testConvId)
    if (!c || !Number.isFinite(v)) return ''
    return (v * c.factor).toFixed(4)
  }, [testValue, testConvId, rows])

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      width={780}
      footer={
        <div className="flex flex-wrap gap-2 justify-between w-full">
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => applyPreset('pine-basic')}>Pine preset</button>
            <button className="btn-secondary" onClick={() => applyPreset('hardwood-basic')}>Hardwood preset</button>
          </div>
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={exportSet}>Export</button>
            <label className="btn-secondary cursor-pointer">
              Import
              <input
                ref={fileRef}
                type="file"
                accept="application/json,.json"
                className="hidden"
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) importSet(f)
                }}
              />
            </label>
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn" onClick={save}>Save</button>
          </div>
        </div>
      }
    >
      {!product ? (
        <div className="text-sm text-[var(--muted)]">No product selected.</div>
      ) : (
        <div className="grid gap-4">
          <div className="overflow-auto rounded-xl border border-[color:var(--border)]">
            <table className="min-w-[680px] w-full text-sm">
              <thead className="bg-[color:var(--surface-1)]">
                <tr className="text-left">
                  <th className="px-3 py-2 w-40">From</th>
                  <th className="px-3 py-2 w-40">To</th>
                  <th className="px-3 py-2 w-40">Factor</th>
                  <th className="px-3 py-2">Note</th>
                  <th className="px-3 py-2 w-36"></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td className="px-3 py-8 text-center text-[var(--muted)]" colSpan={5}>
                      No conversions yet. Add one or use a preset.
                    </td>
                  </tr>
                ) : (
                  rows.map(r => (
                    <tr key={r.id} className="border-t border-[color:var(--border)]">
                      <td className="px-3 py-2">
                        <select className="select" value={r.from} onChange={e => setRow(r.id, 'from', e.target.value)}>
                          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select className="select" value={r.to} onChange={e => setRow(r.id, 'to', e.target.value)}>
                          {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <input className="input" type="number" step="0.0001" value={String(r.factor)} onChange={e => setRow(r.id, 'factor', Number(e.target.value))}/>
                      </td>
                      <td className="px-3 py-2">
                        <input className="input" value={r.note || ''} onChange={e => setRow(r.id, 'note', e.target.value)} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-2">
                          <button className="btn-secondary" onClick={() => invertRow(r.id)}>Invert</button>
                          <button className="btn-secondary" onClick={() => removeRow(r.id)}>Remove</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div>
            <button className="btn-secondary" onClick={addRow}>Add conversion</button>
          </div>

          {rows.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end p-3 rounded-lg bg-[color:var(--surface-1)] border border-[color:var(--border)]">
              <div>
                <label className="text-sm">Value</label>
                <input className="input" value={testValue} onChange={e => setTestValue(e.target.value)} />
              </div>
              <div>
                <label className="text-sm">Using conversion</label>
                <select className="select" value={testConvId} onChange={e => setTestConvId(e.target.value)}>
                  {rows.map(r => (
                    <option key={r.id} value={r.id}>{r.from} → {r.to} × {r.factor}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm">Result</label>
                <input className="input" readOnly value={testResult} />
              </div>
            </div>
          )}

          <div className="text-xs text-[var(--muted)]">
            These conversion factors vary by species, product spec, region, and mill. Use values approved by your operation.
          </div>
        </div>
      )}
    </Drawer>
  )
}
